/**
 * Feb 2026 — shared poller for the Zoho push orchestrator's bounded-wait
 * pattern. When the backend returns `status: "pending"` with a `job_id`,
 * call `pollJobUntilDone(job_id)` to drive the user through the
 * background completion with consistent UX across Dispatch + RM Inward.
 *
 * Contract (matches `services/zoho_push_orchestrator.py`):
 *   • `/api/jobs/{job_id}` returns
 *       { status: "PENDING"|"COMPLETED"|"FAILED", result, error, error_code, ... }
 *   • Polls every 2s, up to a 60s total budget.
 *   • Resolves with the final job document on COMPLETED/FAILED.
 *   • Rejects (`reason="timeout"`) after 60s — caller shows the
 *     "Check later" toast.
 *
 * Returns: { status, result?, error?, error_code? } | throws.
 */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export async function pollJobUntilDone(jobId, {
  intervalMs = 2000,
  budgetMs = 60_000,
  onTick = null,    // optional: called every poll with the current job doc
} = {}) {
  const started = Date.now();
  // First poll happens immediately so super-fast completions don't wait 2s.
  while (true) {
    let job;
    try {
      const res = await axios.get(`${API}/jobs/${jobId}`);
      job = res.data;
    } catch (err) {
      // 404 means the job vanished — treat as terminal failure so caller
      // can surface a clear message instead of polling silently for 60s.
      if (err?.response?.status === 404) {
        return {
          status: "FAILED",
          error: "Job vanished (no job_runs row)",
          error_code: "JOB_NOT_FOUND",
        };
      }
      // Transient network blip — log and continue polling within budget.
      // (If the budget then expires we'll still surface a "Check later".)
      console.warn("pollJobUntilDone transient error", err);
    }
    if (job && (job.status === "COMPLETED" || job.status === "FAILED")) {
      return job;
    }
    if (onTick && job) onTick(job);
    if (Date.now() - started >= budgetMs) {
      const err = new Error("Background job polling exceeded 60s budget");
      err.reason = "timeout";
      err.jobId = jobId;
      throw err;
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
}
